import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Modal, Select, Spin, Tooltip, message } from "antd";
import { AlertTriangle, Archive, ArrowRight, Loader, PowerOff, RefreshCw, RotateCcw, ShieldCheck, UserRound } from "lucide-react";
import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import { card, PanelHead, InlineValue, Note, StatusDot } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import { proxyLiveness } from "../../../pages/enduser/ssh-proxy/sshProxyModel";
import {
  accountNameError,
  listEndpointAccounts,
  getPasswordSsh,
  listGrants,
  listJumpServer,
  MAX_ENDPOINT_ACCOUNTS,
  recommissionEndpoint,
  reconcileSetup,
  scanAccounts,
  setEndpointAccounts,
  updateEndpointGateway,
} from "../../../service/sshProxyService";
import { isAdminUser } from "../../../utils/helpers";
import {
  DecommissionDialog,
  passwordsKeptText,
  decommissionJobFailure,
  recommissionConfirm,
  recommissionError,
  ScanStatusBody,
  SetupScriptBody,
  setupErrorText,
  SourceTag,
  useDecommissionJobs,
  useScanStatus,
} from "./setupScan";

// The endpoint's Gateway Access tab: its accounts, the gateway that reaches it,
// the one-off machine setup and the account scan. These are properties of THE
// TARGET, not of the gateway — one gateway serves many hosts — so they live on
// the endpoint's detail page rather than only in a create form: agent-onboarded
// machines never pass through one.
//
// The gateway reaches the machine with a per-session grant key that Authnull's
// authnull-svc account installs there. Setup creates that account; its key is
// kept per endpoint in the vault, not per gateway, so switching gateways needs
// no new setup.
//
// `address` is the string the gateway dials, the SSH policy names and the
// operator types — never the display hostname. The gateway matches hosts
// exactly, so a hostname like web-vm-04 never matches 192.168.122.175.

export const gatewayLabel = (g) => (g.serverName ? `${g.serverName} (${g.publicIpAddress || "—"})` : `Gateway #${g.id}`);

const capitalise = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// updateEndpointGateway's failure in words. `gatewayGone`: the chosen gateway
// no longer exists, so the list should be re-read.
export function gatewaySaveError(err) {
  const status = err?.response?.status;
  const serverMsg = err?.response?.data?.error || "";
  if (status === 403) return { text: "Administrator role required" };
  if (status === 404 && /endpoint/i.test(serverMsg)) return { text: "This endpoint no longer exists" };
  if (status === 404) return { text: "Gateway no longer exists", gatewayGone: true };
  return { text: serverMsg || "Couldn't update the gateway" };
}

// "Change gateway…" from the endpoint details menu: pick the gateway that
// reaches this endpoint (or none). onSaved(jumpServerId | null) after the save.
export function ChangeGatewayDialog({ open, instanceId, jumpServerId, onClose, onSaved }) {
  const [gateways, setGateways] = useState(null); // null while loading
  const [draft, setDraft] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadGateways = useCallback(() => {
    setGateways(null);
    listJumpServer({
      payload: { pageSize: 200 },
      onSuccess: (data) => setGateways(data?.jumpServers || []),
      onFailure: () => setGateways([]),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setDraft(jumpServerId || undefined);
    setSaving(false);
    setError("");
    loadGateways();
  }, [open, jumpServerId, loadGateways]);

  const options = [
    ...(jumpServerId ? [{ value: 0, label: "None — remove the gateway" }] : []),
    ...(gateways || []).map((g) => ({ value: g.id, label: gatewayLabel(g) })),
  ];
  const changed = draft !== undefined && String(Number(draft) || 0) !== String(Number(jumpServerId) || 0);

  const save = () => {
    const next = Number(draft) || 0;
    setSaving(true);
    setError("");
    updateEndpointGateway({
      instanceId,
      jumpServerId: next,
      onSuccess: () => {
        setSaving(false);
        onSaved && onSaved(next || null);
      },
      onFailure: (err) => {
        setSaving(false);
        const e = gatewaySaveError(err);
        setError(e.text);
        if (e.gatewayGone) {
          setDraft(undefined);
          loadGateways();
        }
      },
    });
  };

  return (
    <Modal
      open={open}
      title="Change gateway"
      okText="Save"
      onOk={save}
      okButtonProps={{ disabled: !changed }}
      confirmLoading={saving}
      onCancel={saving ? undefined : onClose}
      maskClosable={!saving}
      destroyOnClose
      width={520}
    >
      <div style={{ display: "grid", gap: 12, paddingTop: 4, fontFamily: AN_FONT }}>
        <div style={{ fontSize: 12.5, color: AN_COLORS.muted, lineHeight: 1.5 }}>
          The SSH gateway that reaches this endpoint. Operators connect to the gateway; it opens the second connection to
          this host.
        </div>
        <Select
          showSearch
          optionFilterProp="label"
          loading={gateways === null}
          placeholder={gateways && gateways.length === 0 ? "No gateways onboarded yet" : "Choose a gateway"}
          value={draft}
          onChange={(v) => {
            setDraft(v);
            setError("");
          }}
          options={options}
          style={{ width: "100%" }}
        />
        {/* The machine's setup belongs to the endpoint, not the gateway. */}
        {jumpServerId && changed && Number(draft) > 0 && (
          <Note tone="info">
            Re-run setup isn't needed after switching gateways — the machine's setup isn't tied to a gateway.
          </Note>
        )}
        {error && <Note tone="danger">{error}</Note>}
      </div>
    </Modal>
  );
}

const STATUS_TONE = { done: "ok", todo: "warn", blocked: "idle", waiting: "warn", failed: "danger" };

export function StepCard({ n, title, desc, status, statusLabel, danger, children }) {
  // "blocked" is a step that no longer applies (a decommissioned endpoint): greyed.
  // `danger` marks a destructive step (decommission) with a red edge.
  const blocked = status === "blocked";
  return (
    <div
      style={{
        ...card,
        padding: 18,
        ...(danger ? { borderColor: "var(--an-dangerBd)" } : null),
        ...(blocked ? { opacity: 0.6, background: AN_COLORS.bg } : null),
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <span
          style={{
            width: 24, height: 24, borderRadius: "50%", flexShrink: 0, display: "inline-flex",
            alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, fontFamily: AN_FONT,
            background: status === "done" ? AN_COLORS.success : blocked ? AN_COLORS.line : AN_COLORS.primaryL,
            color: status === "done" ? "#fff" : blocked ? AN_COLORS.muted : AN_COLORS.primary,
          }}
        >
          {n}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <PanelHead
            title={title}
            desc={desc}
            right={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: AN_COLORS.muted, fontFamily: AN_FONT, whiteSpace: "nowrap" }}>
                <StatusDot tone={STATUS_TONE[status] || "idle"} />
                {statusLabel}
              </span>
            }
          />
          {children}
        </div>
      </div>
    </div>
  );
}

// Tags input for account names, validated with the backend's rule. Shared by
// agentless onboarding and the endpoint's Gateway Access tab.
export function AccountsInput({ value, onChange, disabled, placeholder = "ubuntu, deploy…" }) {
  return (
    <Select
      mode="tags"
      value={value}
      onChange={(v) => onChange(v.map((a) => String(a).trim()).filter(Boolean))}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width: "100%" }}
      tokenSeparators={[",", " "]}
      open={false}
      suffixIcon={null}
    />
  );
}

// Returns the first problem with a list of account names, or null.
export const accountsError = (names) =>
  names.length > MAX_ENDPOINT_ACCOUNTS
    ? `At most ${MAX_ENDPOINT_ACCOUNTS} accounts per endpoint`
    : names.map(accountNameError).find(Boolean) || null;

// The endpoint's local accounts. Accounts the scan found (source "gateway") or
// the agent read from the machine are shown but can't be edited here; the
// manual ones are replaced as a set on Save. `onChange` receives the full list
// ({ name, source }) after every load and save. Bump `refreshKey` to reload,
// e.g. after a scan finishes.
export function AccountsEditor({ instanceId, onChange, refreshKey, readOnly = false }) {
  const [messageApi, msgCtx] = message.useMessage();
  const [loaded, setLoaded] = useState(null); // null while loading
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const apply = useCallback(
    (list) => {
      setLoaded(list);
      setDraft(list.filter((a) => a.source === "manual").map((a) => a.name));
      onChange && onChange(list);
    },
    [onChange],
  );

  useEffect(() => {
    if (!instanceId) return;
    listEndpointAccounts({
      instanceIds: [instanceId],
      onSuccess: (rows) => apply(rows[0]?.accounts || []),
      onFailure: () => apply([]),
    });
  }, [instanceId, apply, refreshKey]);

  const scannedAccounts = (loaded || []).filter((a) => a.source === "gateway");
  const agentAccounts = (loaded || []).filter((a) => a.source !== "manual" && a.source !== "gateway");
  const savedManual = (loaded || []).filter((a) => a.source === "manual").map((a) => a.name);
  const changed = JSON.stringify(draft) !== JSON.stringify(savedManual);
  const error = accountsError(draft);

  const save = () => {
    setSaving(true);
    setEndpointAccounts({
      instanceId,
      accounts: draft,
      onSuccess: (res) => {
        setSaving(false);
        apply(res?.accounts || []);
        messageApi.success("Accounts saved");
      },
      onFailure: (err) => {
        setSaving(false);
        const status = err?.response?.status;
        messageApi.error(
          status === 403 ? "Administrator role required" : err?.response?.data?.error || "Couldn't save the accounts",
        );
      },
    });
  };

  return (
    <div style={{ ...card, padding: 18 }}>
      {msgCtx}
      <PanelHead
        eyebrow="Accounts"
        title="Local accounts on this endpoint"
        desc="The accounts operators can log in as, offered in the Endpoint Policy form. Scanned accounts stay current on their own — the endpoint is re-scanned daily. Add any others by hand."
        right={<UserRound size={18} color={AN_COLORS.muted} />}
      />
      {loaded === null ? (
        <Spin size="small" />
      ) : (
        <div style={{ display: "grid", gap: 10, maxWidth: 620 }}>
          {scannedAccounts.length > 0 && (
            <div style={{ fontSize: 12.5, color: AN_COLORS.ink2, fontFamily: AN_FONT, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
              <SourceTag source="gateway" /> Found by the scan:{" "}
              {scannedAccounts.map((a) => (
                <InlineValue key={a.name}>{a.name}</InlineValue>
              ))}
            </div>
          )}
          {agentAccounts.length > 0 && (
            <div style={{ fontSize: 12.5, color: AN_COLORS.ink2, fontFamily: AN_FONT, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
              <SourceTag source="agent" /> From the agent:{" "}
              {agentAccounts.map((a) => (
                <InlineValue key={a.name}>{a.name}</InlineValue>
              ))}
            </div>
          )}
          {readOnly ? (
            loaded.length === 0 && (
              <span style={{ fontSize: 12.5, color: AN_COLORS.muted, fontFamily: AN_FONT }}>
                No accounts — they were removed when the endpoint was decommissioned.
              </span>
            )
          ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <AccountsInput value={draft} onChange={setDraft} placeholder="Add accounts by hand, e.g. ubuntu, deploy" />
              {error && <div style={{ color: AN_COLORS.danger, fontSize: 12, marginTop: 4 }}>{error}</div>}
            </div>
            <Button type="primary" loading={saving} disabled={!changed || !!error} onClick={save}>
              Save
            </Button>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// `autoSetup` runs Re-run setup once on mount, for the Endpoints list's action
// (confirmed there); `onAutoSetup` lets the page drop the flag from its URL.
// `lifecycle` ("decommissioning" | "decommissioned") turns steps 1–3 read-only:
// setup, scans, gateway changes and the policy link no longer apply. Step 4
// stays live — it decommissions, follows the clean-up, and recommissions.
// `onOpenAccounts` (optional) opens the page's Accounts tab, where password
// SSH exceptions are kept.
// `onLifecycleChange(status)` reports the endpoint's new status ("Decommissioning",
// "Decommissioned" or "Active") so the page's banner and `lifecycle` follow.
export default function CaTrustPanel({
  instanceId,
  jumpServerId: initialJumpServerId,
  address,
  hostName,
  onGatewayChange,
  autoSetup,
  onAutoSetup,
  lifecycle,
  onLifecycleChange,
  onOpenAccounts,
}) {
  const [messageApi, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();
  const isAdmin = isAdminUser();
  const [jumpServerId, setJumpServerId] = useState(initialJumpServerId || null);
  const [gateways, setGateways] = useState(null); // null while loading
  const [editing, setEditing] = useState(!initialJumpServerId);
  const [draft, setDraft] = useState(initialJumpServerId || undefined);
  const [saving, setSaving] = useState(false);
  // Set after switching from one gateway to another, for the reassurance note.
  const [switchedFrom, setSwitchedFrom] = useState(null);
  // reconcileSetup's result, once Re-run setup has been pressed on this visit.
  const [setup, setSetup] = useState({ loading: false, result: null, error: null });
  const [queuing, setQueuing] = useState(false);
  // Bumped when a watched scan finishes, so the accounts card reloads.
  const [accountsKey, setAccountsKey] = useState(0);
  const onScanFinished = useCallback(() => setAccountsKey((k) => k + 1), []);
  const scan = useScanStatus(instanceId, { onFinished: onScanFinished });
  const { watch } = scan;
  const autoSetupRan = useRef(false);
  // Decommission: the dialog ({ force } while open), and the clean-up job once
  // it has ended without cleaning the machine (failed, expired or missing).
  const [decomDialog, setDecomDialog] = useState(null);
  const [decomFailed, setDecomFailed] = useState(null);
  const [recommissioning, setRecommissioning] = useState(false);

  // The accounts that keep direct password SSH here (null while unknown), so
  // the Re-run setup warning can name them instead of guessing "e.g. ubuntu".
  const [pwExceptions, setPwExceptions] = useState(null);
  useEffect(() => {
    setPwExceptions(null);
    if (!instanceId) return undefined;
    let alive = true;
    getPasswordSsh({
      instanceId: Number(instanceId),
      onSuccess: (res) => alive && setPwExceptions(res.allow || []),
      onFailure: () => alive && setPwExceptions([]),
    });
    return () => {
      alive = false;
    };
  }, [instanceId]);

  // Whether an Endpoint Policy already names this host: any grant on it that is
  // active or pending. null while unknown — the "write a policy" link waits.
  const [hasPolicy, setHasPolicy] = useState(null);
  useEffect(() => {
    setHasPolicy(null);
    if (!instanceId) return undefined;
    let alive = true;
    listGrants({
      instanceId,
      onSuccess: (rows) =>
        alive &&
        setHasPolicy(
          rows.some(
            (g) =>
              String(g.endpointId) === String(instanceId) && (g.status === "active" || g.status === "pending"),
          ),
        ),
      // Unknown: offer the link, as before.
      onFailure: () => alive && setHasPolicy(false),
    });
    return () => {
      alive = false;
    };
  }, [instanceId]);

  // A failure belongs to one decommissioning; it goes once the endpoint moves on.
  useEffect(() => {
    if (lifecycle !== "decommissioning") setDecomFailed(null);
  }, [lifecycle]);

  const onDecomFinished = useCallback(
    (_id, job) => {
      if (job.status === "done") {
        messageApi.success("Decommissioned — the gateway cleaned up the machine");
        onLifecycleChange && onLifecycleChange("Decommissioned");
      } else {
        setDecomFailed(job);
      }
    },
    [messageApi, onLifecycleChange],
  );
  const decomJobs = useDecommissionJobs(
    lifecycle === "decommissioning" && !decomFailed ? [instanceId] : [],
    onDecomFinished,
  );
  const decomJob = decomJobs[String(instanceId)] || null;

  useEffect(() => {
    setJumpServerId(initialJumpServerId || null);
    setEditing(!initialJumpServerId);
    setDraft(initialJumpServerId || undefined);
  }, [initialJumpServerId]);

  const loadGateways = useCallback(() => {
    listJumpServer({
      payload: { pageSize: 200 },
      onSuccess: (data) => setGateways(data?.jumpServers || []),
      onFailure: () => setGateways([]),
    });
  }, []);

  useEffect(() => {
    loadGateways();
  }, [loadGateways]);

  const gateway = (gateways || []).find((g) => String(g.id) === String(jumpServerId)) || null;
  const liveness = gateway ? proxyLiveness(gateway.lastSeenAt) : null;

  const save = () => {
    const next = Number(draft) || 0;
    const previous = jumpServerId;
    setSaving(true);
    updateEndpointGateway({
      instanceId,
      jumpServerId: next,
      onSuccess: () => {
        setSaving(false);
        setJumpServerId(next || null);
        setEditing(!next);
        setSwitchedFrom(previous && next && String(previous) !== String(next) ? previous : null);
        messageApi.success(next ? "Gateway saved" : "Gateway removed");
        onGatewayChange && onGatewayChange(next || null);
      },
      onFailure: (err) => {
        setSaving(false);
        const e = gatewaySaveError(err);
        messageApi.error(e.text);
        if (e.gatewayGone) {
          setDraft(undefined);
          loadGateways();
        }
      },
    });
  };

  const runSetup = useCallback(() => {
    setSetup({ loading: true, result: null, error: null });
    reconcileSetup({
      instanceId,
      onSuccess: (result) => {
        setSetup({ loading: false, result, error: null });
        // Setup queues the first scan itself when there's a gateway.
        if (result.scanJobId) watch(result.scanJobId);
      },
      onFailure: (err) => setSetup({ loading: false, result: null, error: setupErrorText(err) }),
    });
  }, [instanceId, watch]);

  useEffect(() => {
    if (!autoSetup || autoSetupRan.current || !instanceId || !isAdmin) return;
    autoSetupRan.current = true;
    onAutoSetup && onAutoSetup();
    runSetup();
  }, [autoSetup, instanceId, isAdmin, onAutoSetup, runSetup]);

  // Every run issues a NEW key and the old one stops working, so say so first.
  const confirmSetup = () =>
    modal.confirm({
      title: "Re-run setup on this endpoint?",
      content:
        "This issues a new key for Authnull's service account, and the current one stops working until the new script is run on the machine. It's for setting up a machine for the first time or recovering one whose key was lost.",
      okText: "Issue a new key",
      cancelText: "Cancel",
      onOk: runSetup,
    });

  const queueScan = () => {
    setQueuing(true);
    scanAccounts({
      instanceId,
      onSuccess: (res) => {
        setQueuing(false);
        messageApi.success("Scan queued");
        watch(res.jobId);
      },
      onFailure: (err) => {
        setQueuing(false);
        const status = err?.response?.status;
        messageApi.error(
          status === 409
            ? "Assign a gateway to this endpoint first"
            : status === 403
            ? "Administrator role required"
            : err?.response?.data?.error || "Couldn't queue the scan",
        );
      },
    });
  };

  const options = [
    ...(jumpServerId ? [{ value: 0, label: "None — remove the gateway" }] : []),
    ...(gateways || []).map((g) => ({ value: g.id, label: gatewayLabel(g) })),
  ];
  const draftChanged = String(Number(draft) || 0) !== String(Number(jumpServerId) || 0);
  const adminOnly = (node) => (isAdmin ? node : <Tooltip title="Administrator role required">{node}</Tooltip>);
  // Decommissioned (or being decommissioned): every step reads as that, greyed.
  const retiredLabel = lifecycle === "decommissioning" ? "Decommissioning" : "Decommissioned";

  // ─── Step 1 · Gateway ──────────────────────────────────────────────────────
  const step1 = (
    <StepCard
      n={1}
      title="Gateway"
      desc="The SSH gateway that reaches this endpoint. Operators connect to the gateway; it opens the second connection to this host."
      status={lifecycle ? "blocked" : jumpServerId ? "done" : "todo"}
      statusLabel={lifecycle ? retiredLabel : jumpServerId ? "Linked" : "Not linked"}
    >
      {jumpServerId && !editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontFamily: AN_FONT, fontSize: 13 }}>
          <ShieldCheck size={16} color={AN_COLORS.success} />
          <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>
            {gateway ? gatewayLabel(gateway) : gateways === null ? "Loading…" : `Gateway #${jumpServerId}`}
          </span>
          {liveness && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: AN_COLORS.muted }}>
              <StatusDot tone={liveness.tone} />
              {liveness.label}
            </span>
          )}
          {!lifecycle && (
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => { setDraft(jumpServerId); setEditing(true); }}>
              Change
            </Button>
          )}
        </div>
      ) : lifecycle ? (
        <span style={{ fontSize: 13, color: AN_COLORS.muted, fontFamily: AN_FONT }}>No gateway.</span>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", maxWidth: 520, flexWrap: "wrap" }}>
          <Select
            showSearch
            optionFilterProp="label"
            loading={gateways === null}
            placeholder={gateways && gateways.length === 0 ? "No gateways onboarded yet" : "Choose a gateway"}
            value={draft}
            onChange={setDraft}
            options={options}
            style={{ flex: 1, minWidth: 240 }}
          />
          <Button type="primary" loading={saving} disabled={draft === undefined || !draftChanged} onClick={save}>
            Save
          </Button>
          {jumpServerId && (
            <Button onClick={() => { setEditing(false); setDraft(jumpServerId); }} disabled={saving}>
              Cancel
            </Button>
          )}
        </div>
      )}
      {/* The authnull-svc key belongs to the endpoint, not the gateway, so a
          switch needs no new setup — say so before the admin goes looking. */}
      {((jumpServerId && editing && draftChanged && Number(draft) > 0) || (switchedFrom && !editing)) && (
        <div style={{ marginTop: 12 }}>
          <Note tone="info">
            Re-run setup isn't needed after switching gateways — the machine's setup isn't tied to a gateway.
          </Note>
        </div>
      )}
    </StepCard>
  );

  // ─── Step 2 · Set up the machine ───────────────────────────────────────────
  const step2 = (
    <StepCard
      n={2}
      title="Set up the machine"
      desc="A one-off script, run as root on the machine, that creates the authnull-svc account the gateway uses to scan accounts and to install and revoke operators' keys. The Add Endpoint wizard issues it for new machines; re-run it only to set up an older machine or recover one."
      status={lifecycle ? "blocked" : setup.result ? "todo" : setup.error ? "failed" : "idle"}
      statusLabel={lifecycle ? retiredLabel : setup.result ? "Run on this host" : setup.error ? "Not issued" : "Done once per machine"}
    >
      {!lifecycle && (
      <div style={{ display: "grid", gap: 12 }}>
        <div>
          {adminOnly(
            <Button
              onClick={confirmSetup}
              loading={setup.loading}
              disabled={!isAdmin}
              icon={<RefreshCw size={13} style={{ verticalAlign: -2, marginRight: 6 }} />}
            >
              Re-run setup
            </Button>,
          )}
        </div>
        {/* Setup applies gateway-only password SSH; a shared account people
            reach directly with a password needs its exception first. */}
        {pwExceptions && pwExceptions.length > 0 ? (
          <Note tone="info">
            Direct password SSH allowed for: <InlineValue>{pwExceptions.join(", ")}</InlineValue> —{" "}
            {onOpenAccounts ? (
              <Button type="link" size="small" style={{ padding: 0, height: "auto", fontSize: 12.5 }} onClick={onOpenAccounts}>
                add others
              </Button>
            ) : (
              "add others"
            )}{" "}
            before re-running setup.
          </Note>
        ) : (
          <Note tone="warn" icon={<AlertTriangle size={15} />}>
            If people reach this machine with a password (e.g. <InlineValue>ubuntu</InlineValue>),{" "}
            {onOpenAccounts ? (
              <Button type="link" size="small" style={{ padding: 0, height: "auto", fontSize: 12.5 }} onClick={onOpenAccounts}>
                add it as an exception
              </Button>
            ) : (
              "add it as an exception"
            )}{" "}
            before re-running setup, or they'll be locked out of direct password SSH.
          </Note>
        )}
        {(setup.result || setup.error) && <SetupScriptBody {...setup} onRetry={runSetup} />}
      </div>
      )}
    </StepCard>
  );

  // ─── Step 3 · Accounts scan ────────────────────────────────────────────────
  const phase = scan.status?.status;
  const step3 = (
    <StepCard
      n={3}
      title="Accounts scan"
      desc="The gateway logs in as authnull-svc and reads the machine's login accounts into the list above. It runs after setup and again every day; scan now to pick up a change sooner."
      status={lifecycle ? "blocked" : phase === "done" ? "done" : phase === "failed" || phase === "expired" ? "failed" : phase === "queued" || phase === "claimed" ? "waiting" : "idle"}
      statusLabel={
        lifecycle ? retiredLabel : phase === "done" ? "Up to date" : phase === "failed" || phase === "expired" ? "Failed" : phase === "queued" || phase === "claimed" ? "In progress" : "Not scanned"
      }
    >
      {!lifecycle && (
      <div style={{ display: "grid", gap: 12 }}>
        <ScanStatusBody
          status={scan.status}
          neverText={
            jumpServerId
              ? "No scan has run on this endpoint yet. Set the machine up, then press Scan accounts."
              : "No scan has run on this endpoint yet. It needs a gateway to scan from — link one above."
          }
        />
        <div>
          {adminOnly(
            <Button
              type="primary"
              onClick={queueScan}
              loading={queuing}
              disabled={!isAdmin || scan.polling}
              icon={<RefreshCw size={13} style={{ verticalAlign: -2, marginRight: 6 }} />}
            >
              Scan accounts
            </Button>,
          )}
        </div>
      </div>
      )}
    </StepCard>
  );

  // ─── Step 4 · Decommission ─────────────────────────────────────────────────
  const onDecommissioned = (res) => {
    setDecomFailed(null);
    if (res.status === "decommissioning") {
      const n = Number(res.grantsEnded) || 0;
      messageApi.info([`${n} grant${n === 1 ? "" : "s"} ended. The gateway is cleaning up the machine.`, passwordsKeptText(res)].filter(Boolean).join(" "));
      onLifecycleChange && onLifecycleChange("Decommissioning");
    } else if (res.status === "decommissioned") {
      if (res.machineCleaned !== false)
        messageApi.info([res.message ? capitalise(res.message) : "Decommissioned", passwordsKeptText(res)].filter(Boolean).join(". "));
      onLifecycleChange && onLifecycleChange("Decommissioned");
    }
  };

  // Recommissioning issues a new setup key; its script is shown in step 2,
  // exactly as Re-run setup's is, and the first scan is followed in step 3.
  const recommission = () =>
    recommissionEndpoint({
      instanceId,
      onSuccess: (result) => {
        setSetup({ loading: false, result, error: null });
        if (result.scanJobId) watch(result.scanJobId);
        messageApi.success("Recommissioned. Run the new setup script on the machine.");
        onLifecycleChange && onLifecycleChange(result.status || "Active");
      },
      onFailure: (err) => {
        const e = recommissionError(err);
        messageApi.error(e.text);
        if (e.active) onLifecycleChange && onLifecycleChange("Active");
      },
    });
  const confirmRecommission = () =>
    modal.confirm({
      ...recommissionConfirm(hostName),
      onOk: () => {
        setRecommissioning(true);
        return recommission().finally(() => setRecommissioning(false));
      },
    });

  const decomPolling = lifecycle === "decommissioning" && !decomFailed;
  const step4 = (
    <StepCard
      n={4}
      title="Decommission"
      desc="Retire this endpoint: every key and grant on it ends at once, then the gateway removes Authnull's setup from the machine. Its sessions, recordings and logs are kept."
      danger={!lifecycle}
      status={lifecycle === "decommissioned" ? "done" : decomFailed ? "failed" : decomPolling ? "waiting" : "idle"}
      statusLabel={
        lifecycle === "decommissioned" ? "Decommissioned" : decomFailed ? "Clean-up failed" : decomPolling ? "Decommissioning" : "In use"
      }
    >
      {lifecycle === "decommissioned" ? (
        <div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: AN_COLORS.ink2, fontFamily: AN_FONT }}>
            <Archive size={15} color={AN_COLORS.muted} />
            Recommissioning makes it Active again and issues a new setup script to run on the machine.
          </div>
          {adminOnly(
            <Button
              onClick={confirmRecommission}
              loading={recommissioning}
              disabled={!isAdmin}
              icon={<RotateCcw size={13} style={{ verticalAlign: -2, marginRight: 6 }} />}
            >
              Recommission
            </Button>,
          )}
        </div>
      ) : decomFailed ? (
        <div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
          <Note tone="danger" icon={<AlertTriangle size={15} />}>
            <strong>{decommissionJobFailure(decomFailed)}</strong> Access has already ended. Try the clean-up again, or —
            if the machine is gone — finish with force.
          </Note>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {adminOnly(
              <Button danger type="primary" disabled={!isAdmin} onClick={() => setDecomDialog({ force: true })}>
                Finish with force…
              </Button>,
            )}
            {adminOnly(
              <Button disabled={!isAdmin} onClick={() => setDecomDialog({ force: false })}>
                Try the clean-up again
              </Button>,
            )}
          </div>
        </div>
      ) : decomPolling ? (
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontFamily: AN_FONT, color: AN_COLORS.ink2 }}>
            <Loader size={15} color={AN_COLORS.warn} />
            Access has ended. The gateway is removing Authnull's setup from the machine…
          </div>
          {/* The job retries on its own; a transient error is shown, not alarmed on. */}
          {decomJob?.error && (
            <div style={{ fontSize: 12, color: AN_COLORS.muted, fontFamily: AN_FONT }}>
              Last attempt: {decomJob.error}
            </div>
          )}
        </div>
      ) : (
        <div>
          {adminOnly(
            <Button
              danger
              disabled={!isAdmin}
              onClick={() => setDecomDialog({ force: false })}
              icon={<PowerOff size={13} style={{ verticalAlign: -2, marginRight: 6 }} />}
            >
              Decommission…
            </Button>,
          )}
        </div>
      )}
    </StepCard>
  );

  const accountsCard = <AccountsEditor instanceId={instanceId} refreshKey={accountsKey} readOnly={!!lifecycle} />;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {msgCtx}
      {modalCtx}
      {accountsCard}
      {step1}
      {step2}
      {step3}
      {step4}
      <DecommissionDialog
        open={!!decomDialog}
        instanceId={instanceId}
        hostName={hostName}
        hasGateway={!!jumpServerId}
        defaultForce={!!decomDialog?.force}
        onClose={() => setDecomDialog(null)}
        onDone={onDecommissioned}
      />

      {jumpServerId && !lifecycle && hasPolicy === false && (
        <Link
          to={`/pam/policy/v2?gateway=${jumpServerId}${address ? `&host=${encodeURIComponent(address)}` : ""}`}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontFamily: AN_FONT, color: AN_COLORS.primary }}
        >
          Next: write a policy naming this host. With no matching policy, sessions are refused.
          <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}
